import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Receipt, Download, Mail, ExternalLink, Loader2, TrendingUp, Store, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Transactions / Receipts dashboard — shown to both consumers and vendors.
 * Lists every paid transaction for tax write-offs + order lookup.
 */
export default function TransactionsPage() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [resendingId, setResendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/me/transactions`, { headers: getAuthHeaders() });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleResend = async (id) => {
    setResendingId(id);
    try {
      const res = await fetch(`${API}/me/transactions/${id}/resend`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) toast.success('Receipt re-sent to your email');
      else toast.error('Re-send failed');
    } finally {
      setResendingId(null);
    }
  };

  const filtered = (data?.transactions || []).filter((r) => {
    if (typeFilter !== 'all' && r.purchase_type !== typeFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (r.receipt_id || '').toLowerCase().includes(q) ||
      (r.purchase_type || '').toLowerCase().includes(q) ||
      (r.vendor_name || '').toLowerCase().includes(q)
    );
  });

  const uniqueTypes = [...new Set((data?.transactions || []).map((r) => r.purchase_type))];

  const downloadCSV = () => {
    const rows = [
      ['Receipt ID', 'Date', 'Type', 'Vendor', 'Amount', 'Currency'],
      ...(data?.transactions || []).map((r) => [
        r.receipt_id,
        r.created_at,
        r.purchase_type,
        r.vendor_name || '',
        r.total,
        (r.currency || 'usd').toUpperCase(),
      ]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `entreprenatives-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-[#9CA3AF] p-12 text-center">Loading transactions…</div>;

  const total = data?.all_time_total ?? 0;
  const count = data?.count ?? 0;

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-syne text-2xl font-bold text-white flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#A78BFA]" /> My Transactions
          </h1>
          <p className="text-sm text-[#9CA3AF] mt-1">Every payment you've made on EntrepreNatives — keep for tax write-offs.</p>
        </div>
        <button
          onClick={downloadCSV}
          disabled={count === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-white/5 border border-white/10 text-white hover:bg-white/10 disabled:opacity-40"
          data-testid="download-csv"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-gradient-to-br from-[#8B5CF6]/10 to-[#3B82F6]/5 border border-[#8B5CF6]/20">
          <p className="text-[#A78BFA] text-[10px] uppercase tracking-wider font-bold mb-1">All-time total</p>
          <p className="font-mono text-2xl font-bold text-white" data-testid="all-time-total">${total.toFixed(2)}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-white/[0.04] border border-white/10">
          <p className="text-[#9CA3AF] text-[10px] uppercase tracking-wider font-bold mb-1">Transactions</p>
          <p className="font-mono text-2xl font-bold text-white">{count}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-white/[0.04] border border-white/10 col-span-2 md:col-span-1">
          <p className="text-[#9CA3AF] text-[10px] uppercase tracking-wider font-bold mb-1">Categories</p>
          <p className="font-mono text-sm font-bold text-white">{Object.keys(data?.totals_by_type || {}).length} types</p>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search receipt # or vendor…"
            className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-[#64748B]"
            data-testid="tx-search"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-white/5 border border-white/10 text-white text-sm rounded-xl px-3 py-2"
          data-testid="tx-type-filter"
        >
          <option value="all">All types</option>
          {uniqueTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-14 rounded-2xl border border-white/10 bg-white/[0.02]">
          <Receipt className="w-10 h-10 text-[#A78BFA]/50 mx-auto mb-3" />
          <p className="text-white font-semibold mb-1">No transactions yet</p>
          <p className="text-[#9CA3AF] text-sm">Make a purchase and you'll see a receipt here.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 overflow-hidden bg-white/[0.02]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.04] text-[#9CA3AF] text-[10px] uppercase tracking-wider">
                <th className="text-left font-bold p-3">Receipt #</th>
                <th className="text-left font-bold p-3 hidden sm:table-cell">Date</th>
                <th className="text-left font-bold p-3">Type</th>
                <th className="text-left font-bold p-3 hidden md:table-cell">Vendor</th>
                <th className="text-right font-bold p-3">Total</th>
                <th className="text-right font-bold p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.receipt_id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`tx-row-${r.receipt_id}`}>
                  <td className="p-3 font-mono text-xs text-[#E5E7EB]">{r.receipt_id}</td>
                  <td className="p-3 text-[#9CA3AF] text-xs hidden sm:table-cell">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#8B5CF6]/15 text-[#C4B5FD] border border-[#8B5CF6]/25">
                      {r.purchase_type}
                    </span>
                  </td>
                  <td className="p-3 text-[#E5E7EB] text-xs hidden md:table-cell">
                    {r.vendor_name ? (
                      <span className="inline-flex items-center gap-1">
                        <Store size={11} className="text-[#A78BFA]" /> {r.vendor_name}
                      </span>
                    ) : <span className="text-[#64748B]">—</span>}
                  </td>
                  <td className="p-3 text-right font-mono text-sm font-bold text-white">${Number(r.total || 0).toFixed(2)}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <a
                        href={`${API}/me/transactions/${r.receipt_id}/pdf`}
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const res = await fetch(`${API}/me/transactions/${r.receipt_id}/pdf`, { headers: getAuthHeaders() });
                            if (!res.ok) { toast.error('Download failed'); return; }
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = `${r.receipt_id}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            toast.error(err.message);
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold text-emerald-300 hover:text-white hover:bg-emerald-500/20"
                        data-testid={`tx-pdf-${r.receipt_id}`}
                        title="Download PDF receipt"
                      >
                        <Download size={10} /> PDF
                      </a>
                      <button
                        onClick={() => handleResend(r.receipt_id)}
                        disabled={resendingId === r.receipt_id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold text-[#A78BFA] hover:text-white hover:bg-white/10 disabled:opacity-40"
                        data-testid={`tx-resend-${r.receipt_id}`}
                        title="Re-send receipt email (with PDF)"
                      >
                        {resendingId === r.receipt_id ? <Loader2 size={10} className="animate-spin" /> : <Mail size={10} />}
                        Email
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 text-xs text-emerald-100/75" data-testid="tx-tax-note">
        <TrendingUp size={14} className="inline mr-1.5 text-emerald-400" />
        <strong className="text-white">Tax tip:</strong> Every receipt above is a valid business expense record. Export to CSV for your accountant, or click the <Mail size={10} className="inline mx-0.5" /> Email button to re-send any receipt.
      </div>
    </div>
  );
}
